import React, { useEffect, useMemo, useState } from "react";
import { Accounts, Button } from "@ui-kit/components/index.es";
// eslint-disable-next-line import/no-unresolved
import { selectors as sessionSelectors } from "reducers/session";
import { resizableRoute } from "pages/_components/Resizable";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import * as i18n from "util/i18n";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as settingsSelectors } from "reducers/settings";
import { actions as fundcorpActions } from "reducers/fundcorp";
import { actions as metrocorpActions } from "reducers/metrocorp";
import { bool, func, shape } from "prop-types";
import { accountContextMenuItems, maskedAccountNumber } from "util/accounts";
import * as config from "util/config";
import { routerActions } from "react-router-redux/actions";
import { actions as statusActions } from "reducers/status";
import { actions as transferActions } from "reducers/transfer";
import { actions as layoutActions } from "reducers/layout";
import { isMobileNative } from "util/device";
import { dispatchV3ShellLayout, isUxMetrocorpAccountImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getPostLoginHomePath, getBankAccountDetailPath } from "util/v3UxConfig";
import { getMetrocorpConsultPath } from "util/metrocorpRoutes";
import getTransferPath from "util/transfers";
import { getAccountsV3Label } from "v3/v3UiKitI18n";
import { isEnabledFundPerformance, prepareFundcorpOperationsEntry } from "util/fundcorp";
import { getTransferUxLabel } from "util/transferInternalTitleLabels";

function AccountsV3Page(props) {
    AccountsV3Page.propTypes = {
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        dataAccountsMetrocorp: shape({}).isRequired,
        dataAccountsBank: shape({}).isRequired,
        dataAccountsFundcorp: shape({}).isRequired,
        activeEnvironment: shape({}).isRequired,
        hideAmount: bool.isRequired,
        fetchingDownload: bool.isRequired,
        location: shape({}).isRequired,
    };
    const {
        dispatch,
        isDesktop,
        dataAccountsMetrocorp,
        dataAccountsBank,
        dataAccountsFundcorp,
        activeEnvironment,
        hideAmount,
        fetchingDownload,
        location,
    } = props;

    const fundcorpSubscribeReturnPath = location ? `${location.pathname}${location.search || ""}` : undefined;

    const confFavoriteAccountMetrocorp = config.getBoolean("frontend.favoriteAccount.Metrocorp.functionalities", false);
    const confFavoriteAccountFundcorp = config.getBoolean("frontend.favoriteAccount.Funds.functionalities", false);
    const enableHideAmounts = config.getBoolean("frontend.hide.accounts.balance.funcionalities", false);
    const enableFundsImprovements = config.getBoolean("frontend.show.FundImprovements.functionalities", false);

    const PermissionMetrocorp = activeEnvironment?.permissions?.metrocorp;
    const PermissionFundcorp = activeEnvironment?.permissions?.fundcorpOperations;
    const showAcountsMetrocorp = confFavoriteAccountMetrocorp && PermissionMetrocorp;
    const showAcountsFundcorp = confFavoriteAccountFundcorp && PermissionFundcorp;
    const metrocorpAccountImprovements = isUxMetrocorpAccountImprovementsEnabled();

    const [showPartialAmounts, setShowPartialAmounts] = useState(false);
    const [actionLoading, setActionLoading] = useState([]);

    const rightContentMobile = useMemo(() => {
        if (!enableHideAmounts || !hideAmount) {
            return null;
        }
        return (
            <Button
                variant="text"
                color="white"
                aria-label="Mostrar saldos durante 3 segundos"
                icon={!showPartialAmounts ? "EyeOff" : "Eye"}
                iconVariant="outline"
                onClick={() => {
                    setShowPartialAmounts(true);
                    setTimeout(() => {
                        setShowPartialAmounts(false);
                    }, 3000);
                }}
            />
        );
    }, [enableHideAmounts, hideAmount, showPartialAmounts]);

    const handleFocus = () => {
        if (isMobileNative) {
            window.location.reload();
        }
    };

    useEffect(() => {
        dispatch(accountsActions.listAccounts());
        dispatch(statusActions.saveLastHref(location));
        dispatch(accountsActions.accountsLastHref(location.pathname));
        window.addEventListener("focus", handleFocus);
        if (showAcountsMetrocorp) {
            dispatch(accountsActions.listAccountsMetrocorp());
        }
        if (showAcountsFundcorp) {
            dispatch(accountsActions.listAccountsFundcorp());
        }

        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [dispatch, location, showAcountsFundcorp, showAcountsMetrocorp]);

    useEffect(() => {
        const homePath = getPostLoginHomePath(true);
        dispatchV3ShellLayout(dispatch, {
            titleKey: "accounts.page.header",
            goBackPath: homePath,
            rightContentMobile,
            scopes: ["accounts", "account/details"],
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [dispatch, rightContentMobile]);

    useEffect(() => {
        if (fetchingDownload) {
            setActionLoading([...actionLoading, "balance"]);
        } else {
            const newActionLoading = actionLoading.filter((action) => action !== "balance");
            setActionLoading(newActionLoading);
        }
    }, [fetchingDownload]);

    const showNewAccountButton = activeEnvironment?.permissions?.accountOpening;
    const permissionTransferAccess = activeEnvironment.permissions.transferAccess;
    const permissionTransferInternal = activeEnvironment.permissions.transferInternal;
    const permissionTransferExternal = activeEnvironment?.permissions?.transferThirdParties;
    const permissionPayAfip = activeEnvironment.permissions["pay.afip"];
    const permissionRequestCheckbook = activeEnvironment?.permissions.requestCheckbook;
    const permissionFundSubscribe = activeEnvironment?.permissions?.fundSubscribe;
    const permissionFundRescue = activeEnvironment?.permissions?.fundRescue;

    const goToDetailAccountMetroCorp = (idAccount) => {
        if (!idAccount) {
            return;
        }
        const selected = (dataAccountsMetrocorp?.accountsArray || []).find(
            (account) => String(account.idAccount) === String(idAccount),
        );
        dispatch(
            routerActions.replace({
                pathname: getMetrocorpConsultPath(),
                state: metrocorpAccountImprovements
                    ? {
                          transition: "transition-drill-out",
                          idAccount,
                          metrocorpHoldingsFilters: {
                              accountId: String(idAccount),
                              speciesId: "all",
                              ...(selected?.verifyCode ? { verifyCode: selected.verifyCode } : {}),
                          },
                      }
                    : { transition: "transition-drill-out", idAccount },
            }),
        );
    };

    const goToDetailAccountBank = (idAccount) =>
        dispatch(
            routerActions.replace({
                pathname: getBankAccountDetailPath(idAccount),
                state: { transition: "transition-drill-out" },
            }),
        );
    const goToDetailAccountFundCorp = (idAccount) => {
        prepareFundcorpOperationsEntry(dispatch, { optionSelected: "movementsAndRequests" });
        dispatch(
            routerActions.replace({
                pathname: "/fundcorpOperations",
                state: { transition: "transition-drill-out", idAccount, return: location?.pathname },
            }),
        );
    };

    const arrayAccountMetroCorp =
        dataAccountsMetrocorp?.accountsArray?.map((account) => ({
            ...account,
            goToDetail: account.idAccount ? goToDetailAccountMetroCorp : undefined,
        })) || [];

    const arrayAccountBank =
        dataAccountsBank?.accountsArray?.map((account) => ({
            ...account,
            goToDetail: goToDetailAccountBank,
        })) || [];

    const arrayAccountFundCorp =
        dataAccountsFundcorp?.accountsArray?.map((account) => ({
            ...account,
            goToDetail: goToDetailAccountFundCorp,
        })) || [];

    dataAccountsMetrocorp.accountsArray = arrayAccountMetroCorp;
    dataAccountsMetrocorp.emptyStateText = metrocorpAccountImprovements
        ? i18n.get("accounts.metrocorp.list.empty")
        : i18n.get("accounts.list.empty");
    dataAccountsBank.accountsArray = arrayAccountBank;
    dataAccountsBank.emptyStateText = i18n.get("accounts.list.empty");
    dataAccountsFundcorp.accountsArray = arrayAccountFundCorp;
    dataAccountsFundcorp.emptyStateText = i18n.get("accounts.list.empty");

    const accountsMetrocorp = showAcountsMetrocorp ? dataAccountsMetrocorp : null;
    const accountsFundcorp = showAcountsFundcorp ? dataAccountsFundcorp : null;

    const handleExport = (format) => dispatch(accountsActions.downloadAccountsList(format));

    const getActionsLabel = (a) => {
        const labels = {
            balance: getAccountsV3Label("accounts.v3.balance.download"),
        };
        if (permissionTransferAccess) {
            labels.transfer = getAccountsV3Label("dashboard.v3.shortcut.transfers");
        }
        if (showNewAccountButton) {
            labels["new-account"] = getAccountsV3Label("accounts.page.newAccount");
        }
        if (permissionFundSubscribe) {
            labels.suscribe = getAccountsV3Label("fund.operation.subscribe");
        }
        if (permissionFundRescue && dataAccountsFundcorp.accountsArray.length > 0) {
            labels.rescue = getAccountsV3Label("fund.operation.rescue");
        }
        labels.extract = getAccountsV3Label("accounts.v3.extract.fundcorp");
        labels["metrocorp.extract"] = getAccountsV3Label("accounts.v3.extract.metrocorp");
        return labels[a];
    };

    const getActionsCallbacks = (a) => {
        const actions = {
            balance: () => handleExport("pdf"),
            "balance.pdf": () => handleExport("pdf"),
            "balance.xls": () => handleExport("xls"),
            "balance.csv": () => handleExport("csv"),
        };
        if (permissionTransferAccess) {
            actions.transfer = () => {
                const redirectPath = getTransferPath("thirdParties");
                dispatch(transferActions.clearTransferData());
                return dispatch(push(redirectPath));
            };
        }
        if (showNewAccountButton) {
            actions["new-account"] = () => {
                dispatch(accountsActions.clearOpeningData());
                dispatch(push("/v3/accounts/create"));
            };
        }

        if (permissionFundSubscribe) {
            actions.suscribe = () =>
                dispatch(
                    fundcorpActions.getInvestorProfile("/fundcorpOperations/subscribe", fundcorpSubscribeReturnPath),
                );
        }
        if (permissionFundRescue && dataAccountsFundcorp.accountsArray.length > 0) {
            actions.rescue = () =>
                dispatch(
                    push({
                        pathname: "/fundcorpOperations/rescue",
                        state: fundcorpSubscribeReturnPath
                            ? { transition: "transition-drill-out", return: fundcorpSubscribeReturnPath }
                            : { transition: "transition-drill-out" },
                    }),
                );
        }

        actions["metrocorp.extract"] = () => {
            dispatch(metrocorpActions.setReportsDrawerVisible(true));
            dispatch(
                push({
                    pathname: getMetrocorpConsultPath(),
                    state: fundcorpSubscribeReturnPath ? { return: fundcorpSubscribeReturnPath } : undefined,
                }),
            );
        };
        actions.extract = () => {
            dispatch(fundcorpActions.setReportsDrawerVisible(true));
            dispatch(
                push({
                    pathname: "/fundcorpOperations",
                    state: fundcorpSubscribeReturnPath ? { return: fundcorpSubscribeReturnPath } : undefined,
                }),
            );
        };
        return actions[a];
    };

    const getContextMenuLabel = (a) => {
        const labels = {};
        if (permissionTransferAccess && permissionTransferInternal) {
            labels["transfer.internal"] = getTransferUxLabel(
                "accounts.actions.transfer.internal",
                "Transferir a mis cuentas",
            );
        }
        if (permissionTransferAccess && permissionTransferExternal) {
            labels["transfer.external"] = "Transferir a otras cuentas";
        }
        if (permissionPayAfip) {
            labels["pay.service"] = "Pagar servicios";
        }
        if (permissionRequestCheckbook) {
            labels.cheqs = "Solicitar nueva chequera";
        }
        if (permissionFundSubscribe) {
            labels.suscribe = i18n.get("fund.operation.subscribe");
        }
        if (permissionFundRescue && dataAccountsFundcorp.accountsArray.length > 0) {
            labels.rescue = i18n.get("fund.operation.rescue");
        }
        if (isEnabledFundPerformance()) {
            labels.performance = i18n.get("fundcorp.performance.contextMenu");
        }
        return labels[a];
    };

    const contextMenuAction = (id, a) => {
        const itemsAcctionsBank = accountContextMenuItems(dispatch, activeEnvironment, id, [], location?.pathname);
        const actions = {
            "transfer.internal": itemsAcctionsBank?.find(
                (acctionBank) => acctionBank.label === "accounts.actions.transfer.internal",
            )?.onClick,
            "transfer.external": itemsAcctionsBank?.find(
                (acctionBank) => acctionBank.label === "accounts.actions.transfer.external",
            )?.onClick,
            "pay.service": itemsAcctionsBank?.find(
                (acctionBank) => acctionBank.label === "accounts.actions.payment.services",
            )?.onClick,
            cheqs: itemsAcctionsBank?.find((acctionBank) => acctionBank.label === "accounts.actions.checkbook")
                ?.onClick,
        };
        if (permissionFundSubscribe) {
            actions.suscribe = () =>
                dispatch(
                    fundcorpActions.getInvestorProfile("/fundcorpOperations/subscribe", fundcorpSubscribeReturnPath),
                );
        }
        if (permissionFundRescue && dataAccountsFundcorp.accountsArray.length > 0) {
            actions.rescue = () =>
                dispatch(
                    push({
                        pathname: "/fundcorpOperations/rescue",
                        state: {
                            transition: "transition-drill-out",
                            idAccount: id,
                            ...(fundcorpSubscribeReturnPath ? { return: fundcorpSubscribeReturnPath } : {}),
                        },
                    }),
                );
        }
        if (isEnabledFundPerformance()) {
            actions.performance = () => {
                dispatch(push(`/fundcorpOperations?opt=performance&c=${id}`));
            };
        }
        return actions[a];
    };

    const handleBack = () => dispatch(push("/v3/dashboard"));

    const setFavoriteStatus = (idProduct, favoriteProductType, favorite) =>
        dispatch(accountsActions.setFavoriteAccount(idProduct, null, !favorite, favoriteProductType));

    const buildHeaderRightOptions = () => {
        const options = [];

        const pushOption = (actionKey, icon, labelKey) => {
            const callback = getActionsCallbacks(actionKey);
            if (!callback) {
                return;
            }
            options.push({
                icon,
                iconVariant: "outline",
                label: getAccountsV3Label(labelKey),
                callback,
            });
        };

        if (showNewAccountButton) {
            pushOption("new-account", "Add", "accounts.page.newAccount");
        }
        pushOption("balance", "SendDown", "accounts.v3.balance.download");
        if (permissionTransferAccess) {
            pushOption("transfer", "Transfer", "dashboard.v3.shortcut.transfers");
        }
        if (permissionFundSubscribe) {
            pushOption("suscribe", "StocksUp", "dashboard.v3.shortcut.fundSubscribe");
        }
        if (permissionFundRescue && dataAccountsFundcorp.accountsArray.length > 0) {
            pushOption("rescue", "RescueFund", "dashboard.v3.shortcut.fundRescue");
        }
        pushOption("extract", "Memo", "accounts.v3.extract.fundcorp");
        pushOption("metrocorp.extract", "Memo", "accounts.v3.extract.metrocorp");

        return options;
    };

    return (
        <Accounts
            accountsBank={dataAccountsBank}
            accountsFundcorp={accountsFundcorp}
            accountsMetrocorp={accountsMetrocorp}
            getActionsCallbacks={getActionsCallbacks}
            getActionsLabel={getActionsLabel}
            handleSetFavorite={setFavoriteStatus}
            hideAmounts={enableHideAmounts && hideAmount && !showPartialAmounts && !isDesktop}
            getContextMenuLabel={getContextMenuLabel}
            contextMenuAction={contextMenuAction}
            actionLoading={actionLoading}
            enableTitleFundcorpv2={enableFundsImprovements}
            enableV3Styles
            headerProps={{
                goBack: handleBack,
                title: getAccountsV3Label("accounts.page.header"),
                rightOptions: buildHeaderRightOptions(),
                renderOptionsAs: "shortcuts",
                renderOptionsInMobile: true,
            }}
        />
    );
}
const mapStateToProps = (state) => {
    const accountsBank = accountsSelectors.getAccounts(state);
    const avaliableBalanceARS = accountsSelectors.getARSTotalBalance(state);
    const avaliableBalanceUSD = accountsSelectors.getUSDTotalBalance(state);
    const loading = accountsSelectors.getFetching(state);

    const accountsArray = accountsBank.map((account) => {
        const numberOfuscado = maskedAccountNumber(account.number);
        const productName = account.productAlias || numberOfuscado;
        const nameWithN = /^\*\*\*\*\d+/.test(productName)
            ? `${i18n.get("global.numero")} ${productName}`
            : productName;
        const productType =
            account.productType === "CA"
                ? i18n.get("accounts.productType.savings")
                : i18n.get("accounts.productType.checking");

        return {
            idAccount: account.idProduct,
            variant: account.favoriteProductType,
            accountLabel: nameWithN,
            accountTypeLabel: productType,
            accountNumber: numberOfuscado,
            currencyLabel: i18n.get(`currency.label.${account.currency}`),
            realBalance: account.countableBalance,
            availableBalance: account.balance,
            isFavorite: account.favorite,
            isOperative: account.favorite,
            isBlockedAccount: !account.active,
        };
    });

    const dataAccountsBank = {
        accountsArray,
        avaliableBalanceARS,
        avaliableBalanceUSD,
        loading,
    };
    return {
        dataAccountsBank,
        dataAccountsFundcorp: accountsSelectors.getDataAccountsFundcorp(state),
        activeEnvironment: sessionSelectors.getActiveEnvironment(state),
        dataAccountsMetrocorp: accountsSelectors.getDataAccountsMetrocorp(state),
        hideAmount: Boolean(settingsSelectors.getUserData(state)?.hideBalance),
        fetchingDownload: accountsSelectors.getFetchingDownload(state),
    };
};

export default compose(connect(mapStateToProps), resizableRoute)(AccountsV3Page);
